import React, { useState, useEffect } from 'react';

const SettingsModal = ({ isOpen, onClose, localStream, onUpdateSettings }) => {
  const [activeTab, setActiveTab] = useState('video');
  const [settings, setSettings] = useState({
    camera: '',
    resolution: '720p',
    microphone: '',
    muteOnJoin: false,
    theme: localStorage.getItem('theme') || 'dark',
    layout: localStorage.getItem('layout') || 'gallery',
    showTimer: true,
    notifications: true,
    autoRecord: false
  });

  useEffect(() => {
    if (isOpen) {
      // Load saved settings from localStorage
      const savedSettings = {
        camera: localStorage.getItem('camera') || '',
        resolution: localStorage.getItem('resolution') || '720p',
        microphone: localStorage.getItem('microphone') || '',
        muteOnJoin: localStorage.getItem('muteOnJoin') === 'true',
        theme: localStorage.getItem('theme') || 'dark',
        layout: localStorage.getItem('layout') || 'gallery',
        showTimer: localStorage.getItem('showTimer') !== 'false',
        notifications: localStorage.getItem('notifications') !== 'false',
        autoRecord: localStorage.getItem('autoRecord') === 'true'
      };
      setSettings(savedSettings);
    }
  }, [isOpen]);

  const handleSave = () => {
    // Save settings to localStorage
    Object.entries(settings).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
    
    if (onUpdateSettings) {
      onUpdateSettings(settings);
    }
    
    showNotification('Settings saved successfully');
    onClose();
  };

  const showNotification = (message) => {
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      top: 80px;
      right: 20px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 12px 24px;
      border-radius: 8px;
      z-index: 9999;
      backdrop-filter: blur(10px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
    `;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => document.body.removeChild(toast), 3000);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Settings</h2>
          <button className="close-modal-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-tabs">
          <button 
            className={`tab-button ${activeTab === 'video' ? 'active' : ''}`}
            onClick={() => setActiveTab('video')}
          >
            Video
          </button>
          <button 
            className={`tab-button ${activeTab === 'audio' ? 'active' : ''}`}
            onClick={() => setActiveTab('audio')}
          >
            Audio
          </button>
          <button 
            className={`tab-button ${activeTab === 'display' ? 'active' : ''}`}
            onClick={() => setActiveTab('display')}
          >
            Display
          </button>
          <button 
            className={`tab-button ${activeTab === 'app' ? 'active' : ''}`}
            onClick={() => setActiveTab('app')}
          >
            App
          </button>
        </div>

        <div className="modal-content">
          {activeTab === 'video' && (
            <div className="settings-section">
              <div className="setting-item">
                <label>Camera Source</label>
                <select 
                  value={settings.camera} 
                  onChange={(e) => setSettings({...settings, camera: e.target.value})}
                  className="settings-input"
                >
                  <option value="">Default Camera</option>
                  {/* Additional camera options would be populated here */}
                </select>
              </div>
              <div className="setting-item">
                <label>Resolution</label>
                <select 
                  value={settings.resolution} 
                  onChange={(e) => setSettings({...settings, resolution: e.target.value})}
                  className="settings-input"
                >
                  <option value="480p">480p</option>
                  <option value="720p">720p (Recommended)</option>
                  <option value="1080p">1080p</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === 'audio' && (
            <div className="settings-section">
              <div className="setting-item">
                <label>Microphone Source</label>
                <select 
                  value={settings.microphone} 
                  onChange={(e) => setSettings({...settings, microphone: e.target.value})}
                  className="settings-input"
                >
                  <option value="">Default Microphone</option>
                  {/* Additional microphone options would be populated here */}
                </select>
              </div>
              <div className="setting-item">
                <label>Mute on Join</label>
                <input 
                  type="checkbox" 
                  checked={settings.muteOnJoin}
                  onChange={(e) => setSettings({...settings, muteOnJoin: e.target.checked})}
                  className="settings-checkbox"
                />
              </div>
            </div>
          )}

          {activeTab === 'display' && (
            <div className="settings-section">
              <div className="setting-item">
                <label>Theme</label>
                <select 
                  value={settings.theme} 
                  onChange={(e) => setSettings({...settings, theme: e.target.value})}
                  className="settings-input"
                >
                  <option value="dark">Dark</option>
                  <option value="light">Light</option>
                </select>
              </div>
              <div className="setting-item">
                <label>Layout</label>
                <select 
                  value={settings.layout} 
                  onChange={(e) => setSettings({...settings, layout: e.target.value})}
                  className="settings-input"
                >
                  <option value="gallery">Gallery</option>
                  <option value="speaker">Speaker</option>
                  <option value="immersive">Immersive</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === 'app' && (
            <div className="settings-section">
              <div className="setting-item">
                <label>Show Meeting Timer</label>
                <input 
                  type="checkbox" 
                  checked={settings.showTimer}
                  onChange={(e) => setSettings({...settings, showTimer: e.target.checked})}
                  className="settings-checkbox"
                />
              </div>
              <div className="setting-item">
                <label>Enable Notifications</label>
                <input 
                  type="checkbox" 
                  checked={settings.notifications}
                  onChange={(e) => setSettings({...settings, notifications: e.target.checked})}
                  className="settings-checkbox"
                />
              </div>
              <div className="setting-item">
                <label>Auto-record on Meeting Start</label>
                <input 
                  type="checkbox" 
                  checked={settings.autoRecord}
                  onChange={(e) => setSettings({...settings, autoRecord: e.target.checked})}
                  className="settings-checkbox"
                />
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="save-btn" onClick={handleSave}>Save Settings</button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;


